import { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Upload, Palette, Trash2 } from "lucide-react";
import { useTheme, THEME_PRESETS } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ColorField = ({ label, value, onChange, tid }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="flex items-center gap-2 mt-1">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-md border border-slate-200 cursor-pointer"
        data-testid={tid}
      />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono uppercase" />
    </div>
  </div>
);

export default function ThemeSettings() {
  const { theme, setTheme, applyPreview } = useTheme();
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const applyPreset = (key) => {
    const p = THEME_PRESETS[key];
    applyPreview({ theme_preset: key, ...p });
  };
  const patch = (k, v) => applyPreview({ [k]: v, theme_preset: "custom" });

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800 * 1024) return toast.error("Logo must be under 800 KB");
    const r = new FileReader();
    r.onload = () => applyPreview({ logo_url: r.result });
    r.readAsDataURL(f);
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await setTheme({});
      toast.success("Theme saved");
    } catch (e) {
      toast.error("Could not save theme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="theme-settings">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-slate-500" />
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Theme &amp; Branding</div>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving} data-testid="save-theme">
          {saving ? "Saving…" : "Save Theme"}
        </Button>
      </div>

      <div className="p-5 space-y-6">
        {/* Presets */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Preset Themes</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(THEME_PRESETS).map(([key, p]) => {
              const selected = theme.theme_preset === key;
              return (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  data-testid={`preset-${key}`}
                  className={`text-left rounded-md border overflow-hidden transition-colors ${selected ? "border-slate-900" : "border-slate-200 hover:border-slate-400"}`}
                >
                  <div className="h-10 flex items-center px-3" style={{ background: p.header_color, color: p.header_text_color }}>
                    <span className="text-[11px] font-bold uppercase tracking-wider">{p.name}</span>
                    {selected && <Check className="w-3.5 h-3.5 ml-auto" />}
                  </div>
                  <div className="p-2 flex items-center gap-2 bg-white">
                    <span className="w-5 h-5 rounded" style={{ background: p.accent_color }} />
                    <span className="text-[10px] font-mono text-slate-500">{p.accent_color}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom colors */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Custom Colors</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ColorField label="Top banner" value={theme.header_color} onChange={(v) => patch("header_color", v)} tid="color-header" />
            <ColorField label="Accent · buttons" value={theme.accent_color} onChange={(v) => patch("accent_color", v)} tid="color-accent" />
            <ColorField label="Sidebar" value={theme.sidebar_color} onChange={(v) => patch("sidebar_color", v)} tid="color-sidebar" />
            <ColorField label="Header text" value={theme.header_text_color} onChange={(v) => patch("header_text_color", v)} tid="color-header-text" />
          </div>
        </div>

        {/* Logo */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Company Logo</div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {theme.logo_url ? <img src={theme.logo_url} alt="logo" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">No logo</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="logo-upload">
              <Upload className="w-4 h-4 mr-1.5" /> Upload logo
            </Button>
            {theme.logo_url && (
              <Button variant="outline" onClick={() => applyPreview({ logo_url: "" })} data-testid="logo-remove">
                <Trash2 className="w-4 h-4 mr-1.5" /> Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-2">PNG/JPG under 800 KB. Replaces the hard-hat mark in the sidebar and top of PDFs.</p>
        </div>

        {/* Live preview */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Live Preview</div>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <div className="brand-banner px-4 py-3 flex items-center gap-3">
              {theme.logo_url ? <img src={theme.logo_url} alt="" className="w-6 h-6 rounded object-cover" /> : <div className="w-6 h-6 rounded bg-white/20" />}
              <div className="brand-banner-text font-display font-extrabold">{theme.company_name || "QuickQuote"}</div>
              <span className="brand-banner-text text-xs opacity-80 ml-auto">Header preview</span>
            </div>
            <div className="grid grid-cols-3" style={{ minHeight: 120 }}>
              <div className="brand-sidebar p-3 border-r border-slate-200 col-span-1 space-y-1">
                <div className="nav-item active px-3 py-1.5 rounded text-xs font-medium">Dashboard</div>
                <div className="nav-item px-3 py-1.5 rounded text-xs font-medium">Customers</div>
                <div className="nav-item px-3 py-1.5 rounded text-xs font-medium">Quotes</div>
              </div>
              <div className="col-span-2 bg-slate-50 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Revenue</div>
                <div className="font-display text-2xl font-extrabold" style={{ color: theme.accent_color }}>$12,480</div>
                <div className="mt-2 flex gap-2">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-md">Primary</button>
                  <button className="text-blue-600 border border-slate-200 text-xs px-3 py-1.5 rounded-md">Link</button>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Changes preview instantly. Click <b>Save Theme</b> to persist for your account and all PDFs.</p>
        </div>
      </div>
    </div>
  );
}
